import cn from 'classnames';
import { MdFileDownload } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { getHeatCellColors, MatrixRow } from '../../alexa-visibility-competitive-utils';
import { isSameBrand } from '../../alexa-visibility-utils';

interface CompetitiveMatrixProps {
  rows: MatrixRow[];
  brands: string[];
  targetBrand: string;
  getBrandColor: (brand: string) => string;
  onDownloadPng: () => void;
}

// Every query against each brand: the share of that query's responses that mention the brand,
// shaded in the brand's own color (darker = more visible).
export const CompetitiveMatrix = ({
  rows,
  brands,
  targetBrand,
  getBrandColor,
  onDownloadPng
}: CompetitiveMatrixProps) => (
  <div className='aars-chart-card aars-comp-matrix-card' id='competitive-matrix'>
    <div className='aars-chart-card-header'>
      <div className='aars-chart-card-title'>
        Query × brand visibility matrix
        <span className='aars-chart-card-subtitle'>
          % of each query&apos;s runs where the brand appeared in Alexa&apos;s response — darker =
          more visible · sorted by {targetBrand} visibility · all runs
        </span>
      </div>
      <Button
        size={Size.SM}
        appearance={Appearance.OUTLINED}
        className='no-print'
        data-html2canvas-ignore='true'
        leadingElement={<MdFileDownload />}
        onClick={onDownloadPng}
      >
        PNG
      </Button>
    </div>
    <div className='aars-comp-matrix-scroll'>
      <table className='aars-comp-matrix'>
        <thead>
          <tr>
            <th className='aars-comp-matrix-query'>Query</th>
            {brands.map(brand => (
              <th
                key={brand}
                className={cn('aars-comp-matrix-brand', {
                  'is-target': isSameBrand(brand, targetBrand)
                })}
                style={{ color: getBrandColor(brand) }}
              >
                {isSameBrand(brand, targetBrand) && <span aria-hidden='true'>● </span>}
                {brand}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.query}>
              <th className='aars-comp-matrix-query' scope='row'>
                {row.query}
              </th>
              {row.cells.map(cell => (
                <td
                  key={cell.brand}
                  className='aars-comp-matrix-cell'
                  style={getHeatCellColors(getBrandColor(cell.brand), cell.percent)}
                  title={`${cell.count} of ${cell.total} responses`}
                >
                  {cell.percent.toFixed(0)}%
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
